import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '../i18n';
import { api } from '../services/api';
import { UniversalSearchResponse } from '../types/search.types';

const DEBOUNCE_MS = 280;

export function useUniversalSearch(isOpen: boolean) {
  const [query, setQuery] = useState('');
  const [data, setData] = useState<UniversalSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    setQuery('');
    setData(null);
    setLoading(false);
    setError(null);
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setData(null);
      setLoading(false);
      setError(null);
      abortRef.current?.abort();
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = ++requestIdRef.current;

      setLoading(true);
      setError(null);

      try {
        const response = await api.get<{ success: boolean; data: UniversalSearchResponse }>('/search', {
          params: { q: trimmed, limit: 8 },
          signal: controller.signal,
        });

        if (requestId !== requestIdRef.current) return;

        if (response.data.success) {
          setData(response.data.data);
        } else {
          setData(null);
          setError(i18n.t('search.failed'));
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if (requestId !== requestIdRef.current) return;
        setData(null);
        setError(err instanceof Error ? err.message : i18n.t('search.failed'));
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query]);

  return {
    query,
    setQuery,
    data,
    loading,
    error,
    reset,
  };
}
